import React, { useCallback } from 'react';
import { Checkbox, Flex, BoxProps } from '@mantine/core';
import { Table as TanstackTable, Row as TanstackRow } from '@tanstack/react-table';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { MantineTableDragHandle } from './MantineTableDragHandle';
import { MantineTableCell } from './MantineTableCell';
import { MantineTableDraggableRow } from './MantineTableDraggableRow';
import {
	TableData,
	RowActionRenderer,
	DetailPanelRenderer,
	RowPropsRenderer,
	CellPropsRenderer,
	getTableMeta,
} from './types';
import { UseTableGridLayoutReturn } from './hooks/useTableGridLayout';
import { getPinnedColumnStyle, LEADING_TRACK_WIDTH } from './utils/pinnedColumn';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';

interface Props<T extends TableData> {
	row: TanstackRow<T>;
	table: TanstackTable<T>;
	staticRowIndex: number;
	gridLayout: UseTableGridLayoutReturn;
	enableRowDragAndDrop: boolean;
	enableRowSelection: boolean;
	showRowSelection: boolean;
	pinSelectionColumn: boolean;
	enableRowActions: boolean;
	renderRowActions?: RowActionRenderer<T>;
	renderRowActionMenuItems?: RowActionRenderer<T>;
	enableExpanding: boolean;
	renderDetailPanel?: DetailPanelRenderer<T>;
	mantineDetailPanelProps?: React.HTMLProps<HTMLDivElement> & BoxProps;
	mantineTableBodyRowProps?: RowPropsRenderer<T>;
	mantineTableBodyCellProps?: CellPropsRenderer<T>;
	getRowId?: (row: T, index: number, parent?: unknown) => string;
	hoveredRowId: string | null;
	setHoveredRowId: (id: string | null) => void;
	hasFooters: boolean;
	enableVerticalCellBorders?: boolean;
}

export const MantineTableRow = <T extends TableData>({
	row,
	table,
	staticRowIndex,
	gridLayout,
	enableRowDragAndDrop,
	enableRowSelection,
	showRowSelection,
	pinSelectionColumn,
	enableRowActions,
	renderRowActions,
	renderRowActionMenuItems,
	enableExpanding,
	renderDetailPanel,
	mantineDetailPanelProps,
	mantineTableBodyRowProps,
	mantineTableBodyCellProps,
	getRowId,
	hoveredRowId,
	setHoveredRowId,
	hasFooters,
	enableVerticalCellBorders = false,
}: Props<T>) => {
	const extraRowProps = mantineTableBodyRowProps
		? mantineTableBodyRowProps({
				row,
				table,
				staticRowIndex,
			})
		: {};

	const isManualPagination = getTableMeta(table).manualPagination;
	const isGreedySelecting = getTableMeta(table).greedySelecting;
	const canSelectRow = row.getCanSelect();

	const shouldInvertSelection = isManualPagination && isGreedySelecting;

	const isRowSelected = shouldInvertSelection ? !row.getIsSelected() : row.getIsSelected();
	const isLastRow = staticRowIndex === table.getRowModel().rows.length - 1;
	const shouldShowBorder = !isLastRow || hasFooters;
	const isRowHovered = hoveredRowId === row.id;

	// Extract style from extraRowProps to avoid overriding our merged styles
	const { style: extraStyle, onClick, ...otherExtraProps } = extraRowProps;

	const baseRowStyles = {
		display: 'flex',
		...(shouldShowBorder && {
			borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
		}),
		...(isRowHovered && {
			backgroundColor: 'rgba(244, 245, 246, 0.60)', // #BDC4C8 with 60% opacity
		}),
	};

	const selectedRowStyles = isRowSelected
		? {
				backgroundColor: defaultTheme.v3.colors.blue[0],
				...(shouldShowBorder && {
					borderBottom: `1px solid ${defaultTheme.v3.colors.blue[2]}`,
				}),
			}
		: {};

	// Merge styles: base -> custom -> selected (custom styles override base, selected override both)
	const mergedRowStyles = {
		...baseRowStyles,
		...(extraStyle || {}),
		...selectedRowStyles,
	};

	const rowId = getRowId ? getRowId(row.original, staticRowIndex) : row.id;
	const tableDataTestId = getTableMeta(table)?.dataTestId ?? `${rowId}-${staticRowIndex}`;

	const visibleCells = row.getVisibleCells();
	const parentColumnMap = new Map<string, string[]>();

	const hasParentColumns = table
		.getHeaderGroups()
		.some((headerGroup) =>
			headerGroup.headers.some((header) => header.subHeaders && header.subHeaders.length > 0)
		);

	if (hasParentColumns) {
		table.getHeaderGroups().forEach((headerGroup) => {
			headerGroup.headers.forEach((header) => {
				const isParentColumn = header.subHeaders && header.subHeaders.length > 0;
				if (isParentColumn) {
					const childColumnIds = header.subHeaders.map((subHeader) => subHeader.id);
					parentColumnMap.set(header.id, childColumnIds);
				}
			});
		});
	}

	const handleRowSelectionChange = useCallback(() => {
		if (!canSelectRow) {
			return;
		}

		const rowSelection = row.getIsSelected();
		if (shouldInvertSelection) {
			table.setRowSelection((prev) => ({
				...prev,
				[row.id]: isRowSelected,
			}));
		} else {
			table.setRowSelection((prev) => ({
				...prev,
				[row.id]: !rowSelection,
			}));
		}
	}, [canSelectRow, shouldInvertSelection, row, table, isRowSelected]);

	return (
		<MantineTableDraggableRow
			row={row}
			rowId={rowId}
			enableRowDragAndDrop={enableRowDragAndDrop}
			gridTemplateColumns={gridLayout.gridTemplateColumns}
		>
			<>
				<div
					style={{
						display: 'grid',
						gridColumn: '1 / -1',
						gridTemplateColumns: gridLayout.gridTemplateColumns,
						cursor: enableRowSelection && showRowSelection ? 'pointer' : undefined,
					}}
					onMouseEnter={() => setHoveredRowId(row.id)}
					onMouseLeave={() => setHoveredRowId(null)}
					onClick={(e) => {
						if (enableRowSelection && showRowSelection) {
							// In selection mode, clicking anywhere on the row toggles selection
							handleRowSelectionChange();
						} else if (onClick) {
							// Normal mode - navigation
							onClick(e);
						}
					}}
					role="row"
					data-testid={`${tableDataTestId}-row-${staticRowIndex}`}
					{...otherExtraProps}
				>
					{enableRowDragAndDrop && (
						<MantineTableDragHandle
							enableRowDragAndDrop={enableRowDragAndDrop}
							rowStyles={mergedRowStyles}
							gridColumn={1}
						/>
					)}
					{enableRowSelection && showRowSelection && (
						<div
							key={`${row.id}-select-row`}
							style={{
								gridColumn: enableRowDragAndDrop ? 2 : 1,
								alignItems: 'center',
								justifyContent: 'center',
								padding: '8px',
								...mergedRowStyles,
								...(pinSelectionColumn &&
									getPinnedColumnStyle(true, Z_INDEX.STICKY_PINNED_COLUMN, {
										backgroundColor: mergedRowStyles.backgroundColor ?? 'white',
										left: enableRowDragAndDrop ? LEADING_TRACK_WIDTH : 0,
										withShadow: false,
									})),
							}}
						>
							<Checkbox
								checked={isRowSelected}
								disabled={!canSelectRow}
								onChange={handleRowSelectionChange}
								onClick={(e) => e.stopPropagation()}
								size="sm"
							/>
						</div>
					)}
					{visibleCells.map((cell) => {
						const isLastColumn =
							visibleCells[visibleCells.length - 1].column.id === cell.column.id;

						return (
							<MantineTableCell
								key={cell.id}
								cell={cell}
								row={row}
								table={table}
								gridLayout={gridLayout}
								mantineTableBodyCellProps={mantineTableBodyCellProps}
								hasParentColumns={hasParentColumns}
								parentColumnMap={parentColumnMap}
								isLastColumn={isLastColumn}
								rowStyles={mergedRowStyles}
								enableVerticalCellBorders={enableVerticalCellBorders}
							/>
						);
					})}
					{enableRowActions && (renderRowActions || renderRowActionMenuItems) && (
						<div
							key={`${row.id}-actions`}
							style={{
								gridColumn: gridLayout.gridColumnCount,
								alignItems: 'center',
								padding: '8px',
								...mergedRowStyles,
							}}
						>
							{renderRowActions
								? renderRowActions({ row, table })
								: renderRowActionMenuItems?.({ row, table })}
						</div>
					)}
				</div>
				{enableExpanding && row.getIsExpanded() && renderDetailPanel && (
					<Flex
						w="100%"
						style={{
							gridColumn: '1 / -1',
							borderTop: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
							...(mantineDetailPanelProps?.style || {}),
						}}
						{...(mantineDetailPanelProps
							? Object.fromEntries(
									Object.entries(mantineDetailPanelProps).filter(
										([key]) => key !== 'style' && key !== 'wrap'
									)
								)
							: {})}
					>
						{renderDetailPanel({ row, table })}
					</Flex>
				)}
			</>
		</MantineTableDraggableRow>
	);
};
